import { Chip, Stack, Typography } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoCard } from 'core/components';
import {
  DATE_MONTH_YEAR_LONG_FORMAT,
  DATE_MONTH_YEAR_SHORT_FORMAT,
  DEFAULT_PLACEHOLDER,
} from 'core/constants';
import { Loader, Tooltip } from 'core/elements';
import { formatDate, getDateRangeValue, getValue } from 'core/utils';
import { CustomerQuoteUpsert } from 'quotes/components/CustomerQuoteUpsert';
import { ShipSelection } from 'quotes/components/CustomerQuoteUpsert/ShipSelectionDateFields/enums';
import { statusColorMap } from 'quotes/constants';
import { ExternalOrderErrorMessageButton, ExternalOrderStatus } from 'quotes/elements';
import {
  OrderType,
  PricingStrategy,
  QuoteEntityType,
  QuoteStatus,
  QuoteStatusLabel,
} from 'quotes/enums';
import { quotePartsPath } from 'quotes/routes/routePaths';
import { ICustomerQuote } from 'quotes/types';
import {
  getDefaultEarliestAcceptableDate,
  getOrderTypeLabels,
  getPricingStrategyLabels,
  getShipSelectionLabels,
} from 'quotes/utils';
import { Color, Level, Size, Variant } from 'theme/enums';

import { getLinkValue } from './utils';

export interface ILoadingFields {
  externalOrderNumber?: boolean;
  externalPendingOrderNumber?: boolean;
}

interface IProps {
  quote: ICustomerQuote;
  updateQuote: (updatedQuote?: ICustomerQuote) => void;
  entityType?: QuoteEntityType;
  isEditable?: boolean;
  loadingFields?: ILoadingFields;
}

export const MainInfo = ({
  quote,
  updateQuote,
  entityType = QuoteEntityType.QUOTE,
  isEditable = true,
  loadingFields,
}: IProps) => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const defaultValues = useMemo(
    () => ({
      ...quote,
      contact: {
        name: quote.customerContactName,
        userId: quote.customerContactUserId,
        companyName: quote.customerContactGroup.name,
      },
      customerNumber: {
        externalCustomerNumber: quote.externalCustomerNumber,
        source: null,
      },
      pricingDate: quote.pricingDate ? dayjs(quote.pricingDate) : null,
      pricingStrategy: quote.alternateVersionEnabled
        ? quote.pricingStrategy ?? PricingStrategy.EXTERNAL
        : null,
      validSince: quote.startDate ? dayjs(quote.startDate) : null,
      validUntil: quote.validUntil ? dayjs(quote.validUntil) : null,
      quoteStatusLostNote: quote.notesMap?.statusLost?.content,
      closeDate: quote.estimatedCloseDate
        ? formatDate(quote?.estimatedCloseDate, false, DATE_MONTH_YEAR_SHORT_FORMAT)
        : null,
      verticalMarket: quote.verticalMarketId
        ? { id: quote.verticalMarketId, name: quote.verticalMarketName }
        : null,
      ...(QuoteEntityType.ORDER === entityType && {
        orderType: quote.orderType || OrderType.BILLABLE,
        shipSelection: quote.shipSelection ?? ShipSelection.NO_DELIVERY_DATE,
        earliestAcceptableDate: getDefaultEarliestAcceptableDate(quote),
        requiredOnSiteDate: quote.requiredOnSiteDate ? dayjs(quote.requiredOnSiteDate) : null,
        shippingMethod: quote.shippingMethodId
          ? { id: quote.shippingMethodId, name: quote.shippingMethodName }
          : null,
        freightTerm: quote.freightTermId
          ? { id: quote.freightTermId, name: quote.freightTermName }
          : null,
      }),
    }),
    [quote, entityType]
  );

  const handleOnClose = (updatedQuote?: ICustomerQuote) => {
    setIsEditOpen(false);
    updateQuote(updatedQuote);
  };

  const items = getMainInfoItems(
    quote,
    entityType,
    loadingFields,
    Boolean(quote.alternateVersionEnabled)
  );

  return (
    <>
      <InfoCard
        title={t('titleMainInfo')}
        items={items}
        onEdit={() => setIsEditOpen(true)}
        isEditable={isEditable}
      />

      <CustomerQuoteUpsert
        isOpen={isEditOpen}
        onClose={handleOnClose}
        defaultValues={defaultValues}
        disabledFields={{
          jobName: true,
          contact: true,
          shipToLocation: true,
          billToLocation: true,
          project: true,
          location: true,
        }}
        hiddenFields={{
          jobName: true,
          contact: true,
          shipToLocation: true,
          billToLocation: true,
          project: true,
          location: true,
        }}
        entityType={entityType}
        onSubmitSuccess={updateQuote}
        quote={quote}
      />
    </>
  );
};

const getMainInfoItems = (
  quote: ICustomerQuote,
  entityType = QuoteEntityType.QUOTE,
  loadingFields?: ILoadingFields,
  isAlternateVersionEnabled?: boolean
) => {
  const {
    sourceQuoteNumberItem,
    createdAtItem,
    confidenceItem,
    statusItem,
    externalStatusItem,
    reasonItem,
    estimatedCloseDateItem,
    pricingDateItem,
    pricingStrategyItem,
    shipSelectionItem,
    earliestAcceptableDateItem,
    requiredOnSiteDateItem,
    verticalMarketItem,
    shippingMethodItem,
    shippingLocationItem,
    freightTermItem,
    validItem,
    externalOrderNumberItem,
    externalPendingOrderNumberItem,
    purchaseOrderNumberItem,
    customerNumberItem,
    orderTypeItem,
  } = getAllMainInfoItems(quote, loadingFields);

  const alternateVersionOrderItems =
    isAlternateVersionEnabled && QuoteEntityType.ORDER === entityType
      ? [
          orderTypeItem,
          shipSelectionItem,
          earliestAcceptableDateItem,
          requiredOnSiteDateItem,
          shippingMethodItem,
          freightTermItem,
          pricingDateItem,
          pricingStrategyItem,
        ]
      : [];

  switch (entityType) {
    case QuoteEntityType.ORDER:
      return [
        sourceQuoteNumberItem,
        createdAtItem,
        externalOrderNumberItem,
        ...(!isAlternateVersionEnabled ? [externalPendingOrderNumberItem] : []),
        statusItem,
        ...(isAlternateVersionEnabled ? [externalStatusItem] : []),
        purchaseOrderNumberItem,
        ...(!isAlternateVersionEnabled ? [customerNumberItem] : []),
        ...alternateVersionOrderItems,
        verticalMarketItem,
        shippingLocationItem,
      ].filter((item) => !!item);
    case QuoteEntityType.QUOTE:
    default:
      return [
        createdAtItem,
        confidenceItem,
        statusItem,
        ...(QuoteStatus.LOST === quote?.status ? [reasonItem] : []),
        estimatedCloseDateItem,
        verticalMarketItem,
        validItem,
        ...(isAlternateVersionEnabled ? [pricingDateItem, pricingStrategyItem] : []),
        shippingLocationItem,
      ].filter((item) => !!item);
  }
};

const getPricingDateDisplayValue = (pricingDate?: string) => {
  if (!pricingDate) {
    return (
      <Typography level={Level.BODY_SM} sx={{ color: 'neutral.500' }}>
        {t('placeholderPricingDateCurrent')}
      </Typography>
    );
  }

  const isExpired = dayjs(pricingDate).isBefore(dayjs(), 'day');

  if (isExpired) {
    return (
      <Tooltip color={Color.WARNING} title={t('warningPricingDateExpired')}>
        <Typography
          level={Level.BODY_SM}
          color={Color.WARNING}
          startDecorator={<Warning weight="fill" />}
        >
          {formatDate(pricingDate)}
        </Typography>
      </Tooltip>
    );
  }

  return formatDate(pricingDate);
};

const getAllMainInfoItems = (quote: ICustomerQuote, loadingFields?: ILoadingFields) => ({
  sourceQuoteNumberItem: {
    label: t('labelSourceQuoteNumber'),
    value: getLinkValue(
      getValue(quote, 'parentId'),
      quote.parentId ? quotePartsPath(quote.parentId) : null
    ),
  },
  createdAtItem: { label: t('labelCreated'), value: formatDate(quote?.createdAt) },
  confidenceItem: {
    label: t('labelConfidence'),
    value: quote.closeConfidence ? `${quote.closeConfidence}%` : DEFAULT_PLACEHOLDER,
  },
  statusItem: {
    label: t('labelStatus'),
    value: (
      <Stack spacing={0.5} sx={{ alignItems: 'flex-end' }}>
        <Chip color={statusColorMap[quote.status]} variant={Variant.SOLID} size={Size.SM}>
          {QuoteStatusLabel[quote.status]}
        </Chip>

        {QuoteStatus.ORDER_ORDER_FAILED === quote.status && (
          <ExternalOrderErrorMessageButton errorMessage={quote?.externalOrderErrorMessage} />
        )}

        {QuoteStatus.ORDER_SUBMISSION_TIMED_OUT === quote.status && (
          <ExternalOrderErrorMessageButton
            errorMessage={quote?.externalOrderErrorMessage}
            title={t('titleSubmissionTimedOutDetails')}
            color={Color.WARNING}
          />
        )}
      </Stack>
    ),
  },
  externalStatusItem: {
    label: t('labelExternalStatus'),
    value: <ExternalOrderStatus value={quote.externalStatus} />,
  },
  reasonItem: {
    label: t('labelReasonQuoteLose'),
    value: getValue(quote, 'notesMap.statusLost.content'),
  },
  estimatedCloseDateItem: {
    label: t('labelEstimatedCloseDate'),
    value: formatDate(quote.estimatedCloseDate, false, DATE_MONTH_YEAR_LONG_FORMAT),
  },
  shipSelectionItem: {
    label: t('labelShipSelection'),
    value: getShipSelectionLabels()[quote.shipSelection ?? ShipSelection.NO_DELIVERY_DATE],
  },
  earliestAcceptableDateItem: {
    label: t('labelEarliestAcceptableDate'),
    value: formatDate(getDefaultEarliestAcceptableDate(quote)),
  },
  requiredOnSiteDateItem: {
    label: t('labelRequiredOnSiteDate'),
    value: formatDate(quote.requiredOnSiteDate),
  },
  verticalMarketItem: {
    label: t('labelVerticalMarket'),
    value: getValue(quote, 'verticalMarketName'),
  },
  shippingMethodItem: {
    label: t('labelShippingMethod'),
    value: getValue(quote, 'shippingMethodName'),
  },
  freightTermItem: {
    label: t('labelFreightTerm'),
    value: getValue(quote, 'freightTermName'),
  },
  pricingDateItem: {
    label: t('labelDefaultPricingDate'),
    value: getPricingDateDisplayValue(quote.pricingDate),
  },
  pricingStrategyItem: {
    label: t('labelDefaultPricingStrategy'),
    value: quote.pricingStrategy
      ? getPricingStrategyLabels()[quote.pricingStrategy] ?? DEFAULT_PLACEHOLDER
      : DEFAULT_PLACEHOLDER,
  },
  shippingLocationItem: {
    label: t('labelDefaultShippingLocation'),
    value: getValue(quote, 'shippingLocation.name'),
  },
  validItem: {
    label: t('labelValid'),
    value: getDateRangeValue(quote.startDate, quote.validUntil),
  },
  externalOrderNumberItem: {
    label: t('labelExternalOrderNumber'),
    value: loadingFields?.externalOrderNumber ? (
      <Loader size={Size.SM} style={{ margin: 0 }} />
    ) : (
      getLinkValue(getValue(quote, 'externalOrderNumber'), quote.externalOrderUrl)
    ),
  },
  externalPendingOrderNumberItem: {
    label: t('labelExternalPendingOrderNumber'),
    value: loadingFields?.externalPendingOrderNumber ? (
      <Loader size={Size.SM} style={{ margin: 0 }} />
    ) : (
      getLinkValue(getValue(quote, 'externalPendingOrderNumber'), quote.externalPendingOrderUrl)
    ),
  },
  purchaseOrderNumberItem: {
    label: t('labelPurchaseOrderNumber'),
    value: getValue(quote, 'purchaseOrderNumber'),
  },
  customerNumberItem: {
    label: t('labelCustomerNumber'),
    value: getValue(quote, 'externalCustomerNumber'),
  },
  orderTypeItem: {
    label: t('labelOrderType'),
    value: (quote.orderType && getOrderTypeLabels()[quote.orderType]) || DEFAULT_PLACEHOLDER,
  },
});
